import type { ReactNode } from "react";
import type { View } from "../../../../../common/types.ts";
import { MobileSheet } from "../../components/MobileSheet.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { MobileStatRow } from "../../mobile/mobilePrimitives.tsx";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { translateInjuryName } from "../../util/injuryNames.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { toWorker } from "../../util/toWorker.ts";
import {
  getPlayerAvailabilityStatus,
  type PlayerAvailabilityState,
} from "../Player/playerStatusMetrics.ts";

type RosterPlayer = View<"roster">["players"][number];

const getAvailabilityText = (state: PlayerAvailabilityState) => {
  if (state === "healthy") {
    return i18n.t("roster.playerQuickHealthy", {
      ns: "ui",
      defaultValue: "Healthy",
    });
  }

  const key =
    state === "availableLimited"
      ? "statusAvailableLimited"
      : state === "shortTerm"
        ? "statusShortTerm"
        : state === "longTerm"
          ? "statusLongTerm"
          : "statusOut";

  return i18n.t(`injuries.${key}`, { ns: "ui" });
};

export const PlayerQuickSheet = ({
  fullProfileHref,
  onClose,
  open,
  player,
  ratingsVisible,
  readonly,
  reasonTags,
  role,
  spectatorReadonly,
  tradeActionAvailable,
}: {
  fullProfileHref: string;
  onClose: () => void;
  open: boolean;
  player: RosterPlayer;
  ratingsVisible: boolean;
  readonly: boolean;
  reasonTags: ReactNode[];
  role: ReactNode;
  spectatorReadonly: boolean;
  tradeActionAvailable: boolean;
}) => {
  const playerName = formatPlayerName(player.firstName, player.lastName);
  const availability = getPlayerAvailabilityStatus(player.injury);
  const healthDetail =
    availability.state !== "healthy"
      ? i18n.t("roster.playerQuickInjury", {
          ns: "ui",
          defaultValue: "{{type}} · {{count}} game(s)",
          type: translateInjuryName(player.injury.type),
          count: player.injury.gamesRemaining,
        })
      : undefined;
  const ratings = ratingsVisible
    ? `${player.ratings.ovr}/${player.ratings.pot}`
    : i18n.t("roster.scoutingRatingsHidden", {
        ns: "ui",
        defaultValue: "Ratings hidden",
      });
  const footer = (
    <div className="d-flex flex-column gap-2 w-100" data-player-quick-sheet-actions="">
      {tradeActionAvailable && !player.untradable ? (
        <button
          className="btn btn-primary w-100"
          data-player-quick-sheet-trade-for=""
          onClick={() => {
            void toWorker("actions", "tradeFor", { pid: player.pid });
          }}
          type="button"
        >
          {i18n.t("roster.tradeFor", {
            ns: "ui",
            defaultValue: "Trade for",
          })}
        </button>
      ) : null}
      <a
        className="btn btn-outline-secondary w-100"
        data-player-quick-sheet-full-profile=""
        href={fullProfileHref}
      >
        {i18n.t("roster.playerQuickFullProfile", {
          ns: "ui",
          defaultValue: "Full player profile",
        })}
      </a>
    </div>
  );

  return (
    <MobileSheet footer={footer} onClose={onClose} open={open} title={playerName}>
      <div data-player-quick-sheet="" data-player-quick-sheet-pid={player.pid}>
        {readonly ? (
          <span className="badge text-bg-secondary mb-3" data-player-quick-sheet-readonly="">
            {i18n.t("roster.mobileReadonlyReviewChip", {
              ns: "ui",
              defaultValue: "Read-only view",
            })}
          </span>
        ) : null}
        {spectatorReadonly ? (
          <p
            className="alert alert-secondary py-2 small"
            data-player-quick-sheet-spectator-readonly=""
            role="status"
          >
            {i18n.t("roster.playerQuickSpectatorReadonly", {
              ns: "ui",
              defaultValue:
                "Spectator mode: the AI manages trades. You can review this player, but management actions are unavailable.",
            })}
          </p>
        ) : null}
        {tradeActionAvailable && player.untradable ? (
          <p className="alert alert-secondary py-2 small" data-player-quick-sheet-untradable="">
            {i18n.t("roster.playerQuickTradeUnavailable", {
              ns: "ui",
              defaultValue: "Trade unavailable. This player can only be reviewed right now.",
            })}
          </p>
        ) : null}
        <div className="mb-3">
          <div className="fw-semibold">
            <PositionText display="full" pos={player.ratings.pos} /> · {role}
          </div>
          {reasonTags.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mt-2" data-player-quick-sheet-reasons="">
              {reasonTags.slice(0, 3).map((tag, index) => (
                <span className="badge rounded-pill text-bg-light border text-body" key={index}>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div data-player-quick-sheet-facts="">
          <div className="mb-2" data-player-quick-availability={availability.state}>
            <MobileStatusMetric
              detail={healthDetail}
              label={i18n.t("roster.playerQuickHealth", {
                ns: "ui",
                defaultValue: "Health",
              })}
              score={availability.score}
              value={getAvailabilityText(availability.state)}
            />
          </div>
          <MobileStatRow
            label={i18n.t("roster.playerQuickAge", {
              ns: "ui",
              defaultValue: "Age",
            })}
            value={player.age}
          />
          <MobileStatRow
            label={i18n.t("roster.playerQuickRatings", {
              ns: "ui",
              defaultValue: "Overall / Potential",
            })}
            value={ratings}
          />
          <MobileStatRow
            label={i18n.t("roster.playerQuickContract", {
              ns: "ui",
              defaultValue: "Contract",
            })}
            value={`${helpers.formatCurrency(player.contract.amount, "M")} · ${player.contract.exp}`}
          />
        </div>
      </div>
    </MobileSheet>
  );
};
